// src/contexts/AdminContext.tsx - FIXED to remove pagination params

import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { AdminService } from '@/services/AdminService';
import {
    SystemStats,
    OrganisationWithCounts,
    CompleteOrganisationDetails,
    CreateCompleteEmployerRequest,
    CreateOrganisationRequest,
    CreateUnitRequest,
    CreateEmployerRequest,
} from '@/lib/models/admin';
import {
    Organisation,
    Unit,
    UpdateOrganisationRequest,
    UpdateUnitRequest,
    UpdateEmployerRequest,
    EmployerWithDetails,
} from '@/lib/models/employer';

// ==================== CONTEXT TYPE ====================

interface AdminContextType {
    // ==================== DASHBOARD DATA ====================
    systemStats: SystemStats | null;
    organisations: OrganisationWithCounts[];
    selectedOrganisation: CompleteOrganisationDetails | null;

    // ==================== USER MANAGEMENT ====================
    users: UserWithDetails[];
    userStats: UserStats | null;
    isLoadingUsers: boolean;
    isLoadingUserStats: boolean;
    usersError: string | null;

    // ==================== LOADING STATES ====================
    isLoadingStats: boolean;
    isLoadingOrganisations: boolean;
    isLoadingOrganisationDetails: boolean;
    isSaving: boolean;

    // ==================== ERROR STATES ====================
    statsError: string | null;
    organisationsError: string | null;
    organisationDetailsError: string | null;
    saveError: string | null;

    // ==================== DASHBOARD FUNCTIONS ====================
    getSystemStats: () => Promise<SystemStats | null>;
    listOrganisations: () => Promise<OrganisationWithCounts[]>; // ✅ FIXED: No params
    getOrganisationDetails: (organisationId: string) => Promise<CompleteOrganisationDetails | null>;
    refreshDashboard: () => Promise<void>;

    // ==================== USER MANAGEMENT FUNCTIONS ====================
    loadUsers: (role?: string) => Promise<void>;
    loadUserStats: () => Promise<void>;

    // ==================== ORGANISATION MANAGEMENT ====================
    createOrganisation: (data: CreateOrganisationRequest) => Promise<Organisation | null>;
    updateOrganisation: (organisationId: string, data: UpdateOrganisationRequest) => Promise<Organisation | null>;
    deleteOrganisation: (organisationId: string) => Promise<boolean>;

    // ==================== UNIT MANAGEMENT ====================
    createUnit: (data: CreateUnitRequest) => Promise<Unit | null>;
    updateUnit: (unitId: string, data: UpdateUnitRequest) => Promise<Unit | null>;
    deleteUnit: (unitId: string) => Promise<boolean>;

    // ==================== EMPLOYER MANAGEMENT ====================
    createCompleteEmployer: (data: CreateCompleteEmployerRequest) => Promise<EmployerWithDetails | null>;
    createEmployer: (data: CreateEmployerRequest) => Promise<EmployerWithDetails | null>;
    updateEmployer: (employerId: string, data: UpdateEmployerRequest) => Promise<EmployerWithDetails | null>;
    deleteEmployer: (employerId: string) => Promise<boolean>;
}

// ==================== USER TYPES ====================

interface EmployerContext {
    unit_id: string;
    unit_name: string;
    organisation_id: string;
    organisation_name: string;
    job_title?: string;
}

interface UserWithDetails {
    id: string;
    email: string;
    first_name: string;
    last_name: string;
    full_name: string;
    initials: string;
    role: string;
    picture_url: string;
    is_verified?: boolean;
    created_at?: string;
    /**
     * Populated only for employer users that are linked to a Unit.
     * Backend returns null/omits for admins, candidates, and orphan employers.
     */
    employer?: EmployerContext;
}

interface UserStats {
    total_users: number;
    users_by_role: { [key: string]: number };
    verified_users: number;
    unverified_users: number;
}

const AdminContext = createContext<AdminContextType | undefined>(undefined);

// ==================== PROVIDER ====================

export const AdminProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { user, isAuthenticated } = useAuth();

    // ==================== STATE ====================

    // Dashboard data
    const [systemStats, setSystemStats] = useState<SystemStats | null>(null);
    const [organisations, setOrganisations] = useState<OrganisationWithCounts[]>([]);
    const [selectedOrganisation, setSelectedOrganisation] = useState<CompleteOrganisationDetails | null>(null);

    // User management data
    const [users, setUsers] = useState<UserWithDetails[]>([]);
    const [userStats, setUserStats] = useState<UserStats | null>(null);
    const [isLoadingUsers, setIsLoadingUsers] = useState(false);
    const [isLoadingUserStats, setIsLoadingUserStats] = useState(false);
    const [usersError, setUsersError] = useState<string | null>(null);

    // Loading states
    const [isLoadingStats, setIsLoadingStats] = useState(false);
    const [isLoadingOrganisations, setIsLoadingOrganisations] = useState(false);
    const [isLoadingOrganisationDetails, setIsLoadingOrganisationDetails] = useState(false);
    const [isSaving, setIsSaving] = useState(false);

    // Error states
    const [statsError, setStatsError] = useState<string | null>(null);
    const [organisationsError, setOrganisationsError] = useState<string | null>(null);
    const [organisationDetailsError, setOrganisationDetailsError] = useState<string | null>(null);
    const [saveError, setSaveError] = useState<string | null>(null);

    // ==================== EFFECTS ====================

    // ✅ Load admin dashboard when authenticated as admin
    useEffect(() => {
        if (isAuthenticated && user?.id) {
            // ✅ Only load dashboard for ADMIN users
            if (user.role === 'admin') {
                console.log('👑 [Admin] Admin user authenticated, loading dashboard...');
                getSystemStats();
                listOrganisations();
            } else {
                console.log(`👑 [Admin] Non-admin user (${user.role}) detected, skipping dashboard load`);
                setSystemStats(null);
                setOrganisations([]);
            }
        } else {
            console.log('👑 [Admin] User not authenticated, clearing dashboard data...');
            setSystemStats(null);
            setOrganisations([]);
        }
    }, [isAuthenticated, user?.id, user?.role]);

    // ==================== DASHBOARD FUNCTIONS ====================

    const getSystemStats = useCallback(async (): Promise<SystemStats | null> => {
        if (user?.role !== 'admin') {
            console.log('⚠️ [Admin] getSystemStats called for non-admin');
            return null;
        }

        setIsLoadingStats(true);
        setStatsError(null);

        try {
            console.log('📊 [Admin] Fetching system statistics...');
            const stats = await AdminService.getSystemStats();

            setSystemStats(stats);
            console.log('✅ [Admin] System stats loaded');
            return stats;
        } catch (error: any) {
            console.error('❌ [Admin] Error loading system stats:', error);
            setStatsError(error?.message || 'Failed to load system statistics');
            return null;
        } finally {
            setIsLoadingStats(false);
        }
    }, [user?.role]);

    // ✅ FIXED: No pagination params
    const listOrganisations = useCallback(async (): Promise<OrganisationWithCounts[]> => {
        if (user?.role !== 'admin') {
            console.log('⚠️ [Admin] listOrganisations called for non-admin');
            return [];
        }

        setIsLoadingOrganisations(true);
        setOrganisationsError(null);

        try {
            console.log('📋 [Admin] Fetching organisations with counts...');
            const orgs = await AdminService.listOrganisationsWithCounts();

            setOrganisations(orgs);
            console.log(`✅ [Admin] Loaded ${orgs.length} organisations`);
            return orgs;
        } catch (error: any) {
            console.error('❌ [Admin] Error loading organisations:', error);
            setOrganisationsError(error?.message || 'Failed to load organisations');
            return [];
        } finally {
            setIsLoadingOrganisations(false);
        }
    }, [user?.role]);

    const getOrganisationDetails = useCallback(async (
        organisationId: string
    ): Promise<CompleteOrganisationDetails | null> => {
        if (user?.role !== 'admin') {
            console.log('⚠️ [Admin] getOrganisationDetails called for non-admin');
            return null;
        }

        setIsLoadingOrganisationDetails(true);
        setOrganisationDetailsError(null);

        try {
            console.log('🔍 [Admin] Fetching organisation details:', organisationId);
            const details = await AdminService.getCompleteOrganisation(organisationId);

            setSelectedOrganisation(details);
            console.log('✅ [Admin] Organisation details loaded');
            return details;
        } catch (error: any) {
            console.error('❌ [Admin] Error loading organisation details:', error);
            setOrganisationDetailsError(error?.message || 'Failed to load organisation details');
            return null;
        } finally {
            setIsLoadingOrganisationDetails(false);
        }
    }, [user?.role]);

    const refreshDashboard = useCallback(async (): Promise<void> => {
        console.log('🔄 [Admin] Refreshing dashboard...');
        await Promise.all([
            getSystemStats(),
            listOrganisations(),
        ]);
    }, [getSystemStats, listOrganisations]);

    // ==================== USER MANAGEMENT ====================

    const loadUsers = useCallback(async (role?: string): Promise<void> => {
        if (user?.role !== 'admin') {
            console.log('⚠️ [Admin] loadUsers called for non-admin');
            return;
        }

        setIsLoadingUsers(true);
        setUsersError(null);

        try {
            console.log('👥 [Admin] Loading users' + (role ? ` (role: ${role})` : '...'));
            const usersList = await AdminService.listUsers(role);

            setUsers(usersList);
            console.log(`✅ [Admin] Loaded ${usersList.length} users`);
        } catch (error: any) {
            console.error('❌ [Admin] Error loading users:', error);
            setUsersError(error?.message || 'Failed to load users');
        } finally {
            setIsLoadingUsers(false);
        }
    }, [user?.role]);

    const loadUserStats = useCallback(async (): Promise<void> => {
        if (user?.role !== 'admin') {
            console.log('⚠️ [Admin] loadUserStats called for non-admin');
            return;
        }

        setIsLoadingUserStats(true);

        try {
            console.log('📊 [Admin] Loading user statistics...');
            const stats = await AdminService.getUserStats();

            setUserStats(stats);
            console.log('✅ [Admin] User stats loaded');
        } catch (error: any) {
            console.error('❌ [Admin] Error loading user stats:', error);
        } finally {
            setIsLoadingUserStats(false);
        }
    }, [user?.role]);

    // ==================== ORGANISATION MANAGEMENT ====================

    const createOrganisation = useCallback(async (
        data: CreateOrganisationRequest
    ): Promise<Organisation | null> => {
        if (user?.role !== 'admin') {
            console.log('⚠️ [Admin] createOrganisation called for non-admin');
            return null;
        }

        setIsSaving(true);
        setSaveError(null);

        try {
            console.log('➕ [Admin] Creating organisation:', data.name);
            const org = await AdminService.createOrganisation(data);

            // Refresh organisations list
            await listOrganisations();

            console.log('✅ [Admin] Organisation created successfully');
            return org;
        } catch (error: any) {
            console.error('❌ [Admin] Error creating organisation:', error);
            setSaveError(error?.message || 'Failed to create organisation');
            return null;
        } finally {
            setIsSaving(false);
        }
    }, [user?.role, listOrganisations]);

    const updateOrganisation = useCallback(async (
        organisationId: string,
        data: UpdateOrganisationRequest
    ): Promise<Organisation | null> => {
        if (user?.role !== 'admin') {
            console.log('⚠️ [Admin] updateOrganisation called for non-admin');
            return null;
        }

        setIsSaving(true);
        setSaveError(null);

        try {
            console.log('💾 [Admin] Updating organisation:', organisationId);
            const org = await AdminService.updateOrganisation(organisationId, data);

            // Refresh organisations list and details if selected
            await listOrganisations();
            if (selectedOrganisation?.organisation.id === organisationId) {
                await getOrganisationDetails(organisationId);
            }

            console.log('✅ [Admin] Organisation updated successfully');
            return org;
        } catch (error: any) {
            console.error('❌ [Admin] Error updating organisation:', error);
            setSaveError(error?.message || 'Failed to update organisation');
            return null;
        } finally {
            setIsSaving(false);
        }
    }, [user?.role, listOrganisations, selectedOrganisation, getOrganisationDetails]);

    const deleteOrganisation = useCallback(async (organisationId: string): Promise<boolean> => {
        if (user?.role !== 'admin') {
            console.log('⚠️ [Admin] deleteOrganisation called for non-admin');
            return false;
        }

        setIsSaving(true);
        setSaveError(null);

        try {
            console.log('🗑️ [Admin] Deleting organisation:', organisationId);
            await AdminService.deleteOrganisation(organisationId);

            // Refresh organisations list
            await listOrganisations();

            // Clear selected organisation if it was deleted
            if (selectedOrganisation?.organisation.id === organisationId) {
                setSelectedOrganisation(null);
            }

            console.log('✅ [Admin] Organisation deleted successfully');
            return true;
        } catch (error: any) {
            console.error('❌ [Admin] Error deleting organisation:', error);
            setSaveError(error?.message || 'Failed to delete organisation');
            return false;
        } finally {
            setIsSaving(false);
        }
    }, [user?.role, listOrganisations, selectedOrganisation]);

    // ==================== UNIT MANAGEMENT ====================

    const createUnit = useCallback(async (data: CreateUnitRequest): Promise<Unit | null> => {
        if (user?.role !== 'admin') {
            console.log('⚠️ [Admin] createUnit called for non-admin');
            return null;
        }

        setIsSaving(true);
        setSaveError(null);

        try {
            console.log('➕ [Admin] Creating unit:', data.name);
            const unit = await AdminService.createUnit(data);

            // Refresh organisation details if it's selected
            if (selectedOrganisation?.organisation.id === data.organisation_id) {
                await getOrganisationDetails(data.organisation_id);
            }

            console.log('✅ [Admin] Unit created successfully');
            return unit;
        } catch (error: any) {
            console.error('❌ [Admin] Error creating unit:', error);
            setSaveError(error?.message || 'Failed to create unit');
            return null;
        } finally {
            setIsSaving(false);
        }
    }, [user?.role, selectedOrganisation, getOrganisationDetails]);

    const updateUnit = useCallback(async (
        unitId: string,
        data: UpdateUnitRequest
    ): Promise<Unit | null> => {
        if (user?.role !== 'admin') {
            console.log('⚠️ [Admin] updateUnit called for non-admin');
            return null;
        }

        setIsSaving(true);
        setSaveError(null);

        try {
            console.log('💾 [Admin] Updating unit:', unitId);
            const unit = await AdminService.updateUnit(unitId, data);

            // Refresh organisation details if selected
            if (selectedOrganisation) {
                await getOrganisationDetails(selectedOrganisation.organisation.id);
            }

            console.log('✅ [Admin] Unit updated successfully');
            return unit;
        } catch (error: any) {
            console.error('❌ [Admin] Error updating unit:', error);
            setSaveError(error?.message || 'Failed to update unit');
            return null;
        } finally {
            setIsSaving(false);
        }
    }, [user?.role, selectedOrganisation, getOrganisationDetails]);

    const deleteUnit = useCallback(async (unitId: string): Promise<boolean> => {
        if (user?.role !== 'admin') {
            console.log('⚠️ [Admin] deleteUnit called for non-admin');
            return false;
        }

        setIsSaving(true);
        setSaveError(null);

        try {
            console.log('🗑️ [Admin] Deleting unit:', unitId);
            await AdminService.deleteUnit(unitId);

            // Refresh organisation details if selected
            if (selectedOrganisation) {
                await getOrganisationDetails(selectedOrganisation.organisation.id);
            }

            console.log('✅ [Admin] Unit deleted successfully');
            return true;
        } catch (error: any) {
            console.error('❌ [Admin] Error deleting unit:', error);
            setSaveError(error?.message || 'Failed to delete unit');
            return false;
        } finally {
            setIsSaving(false);
        }
    }, [user?.role, selectedOrganisation, getOrganisationDetails]);

    // ==================== EMPLOYER MANAGEMENT ====================

    const createCompleteEmployer = useCallback(async (
        data: CreateCompleteEmployerRequest
    ): Promise<EmployerWithDetails | null> => {
        if (user?.role !== 'admin') {
            console.log('⚠️ [Admin] createCompleteEmployer called for non-admin');
            return null;
        }

        setIsSaving(true);
        setSaveError(null);

        try {
            console.log('🏢 [Admin] Creating complete employer setup for:', data.employer.email);
            const employer = await AdminService.createCompleteEmployer(data);

            // Refresh dashboard
            await refreshDashboard();

            console.log('✅ [Admin] Complete employer setup created successfully');
            return employer;
        } catch (error: any) {
            console.error('❌ [Admin] Error creating complete employer:', error);
            setSaveError(error?.message || 'Failed to create employer setup');
            return null;
        } finally {
            setIsSaving(false);
        }
    }, [user?.role, refreshDashboard]);

    const createEmployer = useCallback(async (
        data: CreateEmployerRequest
    ): Promise<EmployerWithDetails | null> => {
        if (user?.role !== 'admin') {
            console.log('⚠️ [Admin] createEmployer called for non-admin');
            return null;
        }

        setIsSaving(true);
        setSaveError(null);

        try {
            console.log('➕ [Admin] Creating employer for existing unit');
            const employer = await AdminService.createEmployer(data);

            // Refresh organisation details if selected
            if (selectedOrganisation) {
                await getOrganisationDetails(selectedOrganisation.organisation.id);
            }

            console.log('✅ [Admin] Employer created successfully');
            return employer;
        } catch (error: any) {
            console.error('❌ [Admin] Error creating employer:', error);
            setSaveError(error?.message || 'Failed to create employer');
            return null;
        } finally {
            setIsSaving(false);
        }
    }, [user?.role, selectedOrganisation, getOrganisationDetails]);

    const updateEmployer = useCallback(async (
        employerId: string,
        data: UpdateEmployerRequest
    ): Promise<EmployerWithDetails | null> => {
        if (user?.role !== 'admin') {
            console.log('⚠️ [Admin] updateEmployer called for non-admin');
            return null;
        }

        setIsSaving(true);
        setSaveError(null);

        try {
            console.log('💾 [Admin] Updating employer:', employerId);
            const employer = await AdminService.updateEmployer(employerId, data);

            // Refresh organisation details if selected
            if (selectedOrganisation) {
                await getOrganisationDetails(selectedOrganisation.organisation.id);
            }

            console.log('✅ [Admin] Employer updated successfully');
            return employer;
        } catch (error: any) {
            console.error('❌ [Admin] Error updating employer:', error);
            setSaveError(error?.message || 'Failed to update employer');
            return null;
        } finally {
            setIsSaving(false);
        }
    }, [user?.role, selectedOrganisation, getOrganisationDetails]);

    const deleteEmployer = useCallback(async (employerId: string): Promise<boolean> => {
        if (user?.role !== 'admin') {
            console.log('⚠️ [Admin] deleteEmployer called for non-admin');
            return false;
        }

        setIsSaving(true);
        setSaveError(null);

        try {
            console.log('🗑️ [Admin] Deleting employer:', employerId);
            await AdminService.deleteEmployer(employerId);

            // Refresh organisation details if selected
            if (selectedOrganisation) {
                await getOrganisationDetails(selectedOrganisation.organisation.id);
            }

            console.log('✅ [Admin] Employer deleted successfully');
            return true;
        } catch (error: any) {
            console.error('❌ [Admin] Error deleting employer:', error);
            setSaveError(error?.message || 'Failed to delete employer');
            return false;
        } finally {
            setIsSaving(false);
        }
    }, [user?.role, selectedOrganisation, getOrganisationDetails]);

    // ==================== CONTEXT VALUE ====================

    const value: AdminContextType = {
        // Dashboard data
        systemStats,
        organisations,
        selectedOrganisation,

        // User management
        users,
        userStats,
        isLoadingUsers,
        isLoadingUserStats,
        usersError,

        // Loading states
        isLoadingStats,
        isLoadingOrganisations,
        isLoadingOrganisationDetails,
        isSaving,

        // Error states
        statsError,
        organisationsError,
        organisationDetailsError,
        saveError,

        // Dashboard functions
        getSystemStats,
        listOrganisations,
        getOrganisationDetails,
        refreshDashboard,

        // User management functions
        loadUsers,
        loadUserStats,

        // Organisation management
        createOrganisation,
        updateOrganisation,
        deleteOrganisation,

        // Unit management
        createUnit,
        updateUnit,
        deleteUnit,

        // Employer management
        createCompleteEmployer,
        createEmployer,
        updateEmployer,
        deleteEmployer,
    };

    return (
        <AdminContext.Provider value={value}>
            {children}
        </AdminContext.Provider>
    );
};

// ==================== HOOK ====================

export const useAdmin = (): AdminContextType => {
    const context = useContext(AdminContext);
    if (context === undefined) {
        throw new Error('useAdmin must be used within an AdminProvider');
    }
    return context;
};